// Highlight anchoring. We quote the exact text plus a prefix and suffix drawn
// from the article's text content, and store character offsets as a fallback.
// This survives minor extraction changes better than offsets alone: if the
// exact quote still appears in the article, we find it even when surrounding
// paragraphs shift.

export interface TextMap {
  text: string;
  // For each character index in `text`, which text node and local offset it maps to.
  nodes: Array<{ node: Text; start: number }>;
}

export function buildTextMap(root: Node): TextMap {
  let text = "";
  const nodes: Array<{ node: Text; start: number }> = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: (n) => {
      const parent = n.parentElement;
      if (!parent) return NodeFilter.FILTER_REJECT;
      const tag = parent.tagName.toLowerCase();
      if (tag === "script" || tag === "style") return NodeFilter.FILTER_REJECT;
      return n.nodeValue && n.nodeValue.length ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
    },
  });
  let node = walker.nextNode() as Text | null;
  while (node) {
    const value = node.nodeValue ?? "";
    if (value.length) {
      nodes.push({ node, start: text.length });
      text += value;
    }
    node = walker.nextNode() as Text | null;
  }
  return { text, nodes };
}

export interface Anchor {
  exact_text: string;
  prefix: string;
  suffix: string;
  start_offset: number | null;
  end_offset: number | null;
}

export interface Range {
  start: number;
  end: number;
}

// Find the best match for an anchor in the text map. Returns null if no match.
export function findRange(map: TextMap, a: Anchor): Range | null {
  const { text } = map;
  if (!a.exact_text) return null;

  const occurrences: number[] = [];
  let i = text.indexOf(a.exact_text);
  while (i !== -1 && occurrences.length < 32) {
    occurrences.push(i);
    i = text.indexOf(a.exact_text, i + 1);
  }

  if (occurrences.length === 1) {
    return { start: occurrences[0], end: occurrences[0] + a.exact_text.length };
  }
  if (occurrences.length > 1 && a.prefix) {
    for (const start of occurrences) {
      const prefixStart = start - a.prefix.length;
      if (prefixStart >= 0 && text.slice(prefixStart, start) === a.prefix) {
        return { start, end: start + a.exact_text.length };
      }
    }
    if (a.suffix) {
      for (const start of occurrences) {
        const end = start + a.exact_text.length;
        if (text.slice(end, end + a.suffix.length) === a.suffix) {
          return { start, end };
        }
      }
    }
  }

  if (a.start_offset != null && a.end_offset != null) {
    const s = a.start_offset;
    const e = a.end_offset;
    if (s >= 0 && e <= text.length && e > s && text.slice(s, e) === a.exact_text) {
      return { start: s, end: e };
    }
    if (occurrences.length > 0) {
      return { start: occurrences[0], end: occurrences[0] + a.exact_text.length };
    }
  }

  if (occurrences.length === 1) {
    return { start: occurrences[0], end: occurrences[0] + a.exact_text.length };
  }
  return null;
}

// Convert a Range in map coordinates to a DOM Range.
export function toDOMRange(map: TextMap, range: Range): globalThis.Range | null {
  const startNode = nodeAt(map, range.start);
  const endNode = nodeAt(map, range.end - 1);
  if (!startNode || !endNode) return null;
  const dom = document.createRange();
  dom.setStart(startNode.node, range.start - startNode.start);
  dom.setEnd(endNode.node, range.end - endNode.start);
  return dom;
}

function nodeAt(map: TextMap, index: number): { node: Text; start: number } | null {
  const nodes = map.nodes;
  let lo = 0, hi = nodes.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const len = nodes[mid].node.nodeValue?.length ?? 0;
    if (index >= nodes[mid].start && index < nodes[mid].start + len) return nodes[mid];
    if (index < nodes[mid].start) hi = mid - 1;
    else lo = mid + 1;
  }
  // End-of-node boundary: prefer the next node if present.
  for (let k = 0; k < nodes.length; k++) {
    const len = nodes[k].node.nodeValue?.length ?? 0;
    if (index === nodes[k].start + len) {
      return nodes[k + 1] ?? nodes[k];
    }
  }
  return null;
}

// Build an Anchor from the current Selection within the article root.
export function anchorFromSelection(
  map: TextMap,
  root: HTMLElement,
  selection: Selection,
): { anchor: Anchor; range: Range } | null {
  if (!selection.rangeCount) return null;
  const sel = selection.getRangeAt(0);
  if (sel.collapsed) return null;
  if (!root.contains(sel.commonAncestorContainer)) return null;

  const start = charIndexFromPoint(map, sel.startContainer, sel.startOffset);
  const end = charIndexFromPoint(map, sel.endContainer, sel.endOffset);
  if (start == null || end == null || end <= start) return null;

  const exact_text = map.text.slice(start, end).trim();
  if (!exact_text) return null;
  const trimmedStart = map.text.indexOf(exact_text, Math.max(0, start - 100));
  const ts = trimmedStart !== -1 ? trimmedStart : start;
  const te = ts + exact_text.length;

  const prefix = map.text.slice(Math.max(0, ts - 32), ts);
  const suffix = map.text.slice(te, Math.min(map.text.length, te + 32));

  return {
    anchor: {
      exact_text,
      prefix,
      suffix,
      start_offset: ts,
      end_offset: te,
    },
    range: { start: ts, end: te },
  };
}

function charIndexFromPoint(map: TextMap, node: Node, offset: number): number | null {
  if (node.nodeType === Node.TEXT_NODE) {
    for (const entry of map.nodes) {
      if (entry.node === node) return entry.start + offset;
    }
    return null;
  }
  const child = node.childNodes[offset] ?? node.childNodes[offset - 1];
  if (child && child.nodeType === Node.TEXT_NODE) {
    for (const entry of map.nodes) {
      if (entry.node === child) {
        return entry.start + (offset === 0 ? 0 : (child.nodeValue?.length ?? 0));
      }
    }
  }
  return null;
}
